import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ContactPageOutlined } from '@mui/icons-material';
import { Grid, Link } from '@mui/material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon, useScrollIntoView } from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { LabeledDataRow } from '~/components/LabeledDataRow/LabeledDataRow';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';

export const CompanyDetailsWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CompanyDetailsWidget' });
  const { t: tLanguage } = useTranslation(undefined, { keyPrefix: 'language' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'company-details' });

  const cube = 'SummaryCompany';

  const filters: Filter[] = [
    {
      member: `${cube}.id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const query = useCubeQuery({
    filters,
    dimensions: [`${cube}.email`, `${cube}.fax`, `${cube}.phoneNumber`, `${cube}.language`, `${cube}.website`],
  });

  const nodesStringValue = (segment: string | number | boolean | null) => {
    if (isNil(segment) || isEmpty(segment)) return undefined;
    return String(segment);
  };

  const nodes = query.resultSet?.rawData()?.map((segment) => ({
    fax: nodesStringValue(segment[`${cube}.fax`]),
    phone: nodesStringValue(segment[`${cube}.phoneNumber`]),
    language: nodesStringValue(segment[`${cube}.language`]),
    website: nodesStringValue(segment[`${cube}.website`]),
  }));

  const isNodesEmpty = isEmpty(nodes) || isNil(nodes);
  const isLoading = query.isLoading;

  return (
    <Widget ref={widgetRef} active={active} data-intercom-target="CompanySummaryWidget">
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<ContactPageOutlined fontSize="small" />} />} />
      <WidgetContent>
        {!isNodesEmpty ? (
          <Grid
            container
            direction="column"
            spacing={4}
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            <Grid container size={12}>
              <LabeledDataRow labelColumnSize={6} label={t('phone')} body={nodes[0].phone ?? NULLISH_VALUE} />
            </Grid>
            <Grid container size={12}>
              <LabeledDataRow labelColumnSize={6} label={t('fax')} body={nodes[0].fax ?? NULLISH_VALUE} />
            </Grid>
            <Grid container size={12}>
              <LabeledDataRow
                labelColumnSize={6}
                label={t('website')}
                body={
                  nodes[0].website ? (
                    <Link color="inherit" href={nodes[0].website} target="_blank">
                      {nodes[0].website}
                    </Link>
                  ) : (
                    NULLISH_VALUE
                  )
                }
              />
            </Grid>
            <Grid container size={12}>
              <LabeledDataRow
                labelColumnSize={6}
                label={t('language')}
                body={nodes[0].language ? tLanguage(nodes[0].language) : NULLISH_VALUE}
              />
            </Grid>
          </Grid>
        ) : isLoading ? (
          <CircularProgressCentered sx={{ position: 'relative' }} />
        ) : (
          <PlaceholderWidget />
        )}
      </WidgetContent>
    </Widget>
  );
};
